import { loadMessages } from "../pages/locales";
import { resolveInterfaceLang } from "./interfaceLang";

/**
 * Signals for iOS Fastlane / UI-test snapshot capture.
 * Native tests can wait on `html[data-screenshot-ready="true"]`.
 */

export function markScreenshotNotReady() {
    try {
        document.documentElement.removeAttribute("data-screenshot-ready");
    } catch {
        /* ignore */
    }
}

export function markScreenshotReady() {
    try {
        document.documentElement.setAttribute("data-screenshot-ready", "true");
    } catch {
        /* ignore */
    }
}

function waitForImage(url) {
    if (!url || typeof url !== "string") return Promise.resolve();
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.referrerPolicy = "no-referrer";
        img.src = url;
    });
}

async function waitForFonts(fontFamily) {
    if (!fontFamily || !document.fonts?.load) return;
    try {
        await Promise.all([
            document.fonts.load(`400 16px ${fontFamily}`),
            document.fonts.load(`600 16px ${fontFamily}`),
            document.fonts.load(`700 16px ${fontFamily}`),
        ]);
        await document.fonts.ready;
    } catch {
        /* ignore */
    }
}

function waitForPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
        });
    });
}

async function waitForLocale(lang) {
    const resolved = resolveInterfaceLang(lang);
    if (!resolved) return;
    try {
        await loadMessages(resolved);
    } catch {
        /* ignore */
    }
}

/** Call when screenshot scene content is loaded and safe to capture. */
export async function signalScreenshotSceneReady({ imageUrl, imageUrls, fontFamily, lang } = {}) {
    markScreenshotNotReady();
    try {
        if (lang) {
            await waitForLocale(lang);
        }
        await waitForFonts(fontFamily);
        const urls = Array.isArray(imageUrls)
            ? imageUrls.filter(Boolean)
            : imageUrl
              ? [imageUrl]
              : [];
        await Promise.all(urls.map((url) => waitForImage(url)));
        await waitForPaint();
        if (lang) {
            document.documentElement.setAttribute("data-screenshot-lang", resolveInterfaceLang(lang));
        }
    } catch {
        /* ignore */
    }
    markScreenshotReady();
}
